import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Briefcase, Loader2, Plus, X, Send, Linkedin, ExternalLink, Sparkles, Pencil, Trash2, ArrowLeft, Check } from "lucide-react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { VaultChat } from "./VaultChat";
import { useAutoGrow } from "../lib/autogrow";

const STARTERS = [
  "Who in the family works in tech?",
  "Who could help me launch a small business?",
  "Find me a potential co-founder or partner here",
];

export function Kinworks() {
  const [tab, setTab] = useState("feed");
  const [profile, setProfile] = useState(undefined); // undefined=loading, null=none
  const [editing, setEditing] = useState(false);

  const loadProfile = async () => {
    try { const { data } = await api.get("/kinworks/profile"); setProfile(data); }
    catch { setProfile(null); }
  };
  useEffect(() => { loadProfile(); }, []);

  if (profile === undefined) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>;

  const isMember = !!profile;

  if (!isMember && !editing) {
    return (
      <div className="pt-4 text-center" data-testid="kin-join">
        <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto mb-3" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
          <Briefcase size={30} className="gold-text" />
        </div>
        <div className="font-serif-display text-3xl">KinWorks</div>
        <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-secondary)" }}>
          The family business hub. Build a professional profile, share your ventures, and connect with relatives for partnerships and mentorship. Create your profile to join.
        </p>
        <motion.button whileTap={{ scale: 0.97 }} onClick={() => setEditing(true)} data-testid="kin-create-profile"
          className="w-full rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
          Create my business profile
        </motion.button>
      </div>
    );
  }

  if (editing) {
    return <ProfileEditor profile={profile} onDone={(p) => { setProfile(p); setEditing(false); setTab("feed"); }} onCancel={() => setEditing(false)} />;
  }

  return (
    <div data-testid="kin-hub" className="flex flex-col" style={{ height: "calc(90dvh - 96px)" }}>
      <div className="flex gap-1 p-1 rounded-xl mb-3 shrink-0" style={{ background: "var(--overlay)" }}>
        {["feed", "members", "ask"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`kin-tab-${t}`}
            className="flex-1 rounded-lg py-2 text-xs font-semibold capitalize"
            style={tab === t ? { background: "var(--accent)", color: "#fff" } : { color: "var(--text-secondary)" }}>
            {t === "ask" ? "Ask AI" : t}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        {tab === "feed" && <Feed onEditProfile={() => setEditing(true)} />}
        {tab === "members" && <Members onEditProfile={() => setEditing(true)} />}
        {tab === "ask" && (
          <VaultChat tool="kinworks" testid="kin-chat" parseNext
            greeting="I know every KinWorks profile and post in your family. Ask me who's working on what, and I'll help you find the right people to connect with."
            starters={STARTERS} placeholder="Ask about the family's work…" height="calc(90dvh - 168px)" />
        )}
      </div>
    </div>
  );
}

function ProfileEditor({ profile, onDone, onCancel }) {
  const [headline, setHeadline] = useState(profile?.headline || "");
  const [skills, setSkills] = useState(profile?.skills || "");
  const [aspirations, setAspirations] = useState(profile?.aspirations || "");
  const [linkedin, setLinkedin] = useState(profile?.linkedin || "");
  const [ventures, setVentures] = useState(profile?.ventures?.length ? profile.ventures : [{ name: "", link: "" }]);
  const [saving, setSaving] = useState(false);

  const setV = (i, k, v) => setVentures((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addV = () => setVentures((arr) => [...arr, { name: "", link: "" }]);
  const rmV = (i) => setVentures((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/kinworks/profile", {
        headline, skills, aspirations, linkedin,
        ventures: ventures.filter((v) => v.name || v.link),
      });
      toast.success("Profile saved");
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const field = "w-full rounded-xl px-4 py-3 text-sm outline-none";
  const fstyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 };

  return (
    <div className="pt-1 pb-4" data-testid="kin-profile-editor">
      <div className="font-serif-display text-2xl mb-4">Your business profile</div>
      <div className="space-y-3">
        <div>
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>Headline</label>
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Product designer & ceramics maker" data-testid="kin-headline" className={field} style={fstyle} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>Skills & experience</label>
          <textarea value={skills} onChange={(e) => setSkills(e.target.value)} rows={3} placeholder="What you're good at, roles you've held…" data-testid="kin-skills" className={field} style={fstyle} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>Career / business aspirations</label>
          <textarea value={aspirations} onChange={(e) => setAspirations(e.target.value)} rows={3} placeholder="Where you want to go, what you're building toward…" data-testid="kin-aspirations" className={field} style={fstyle} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>Current ventures / projects</label>
          <div className="space-y-2">
            {ventures.map((v, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`kin-venture-${i}`}>
                <div className="flex-1 space-y-1.5">
                  <input value={v.name} onChange={(e) => setV(i, "name", e.target.value)} placeholder="Project name" className={field} style={fstyle} />
                  <input value={v.link} onChange={(e) => setV(i, "link", e.target.value)} placeholder="https://link (optional)" className={field} style={fstyle} />
                </div>
                <button onClick={() => rmV(i)} className="h-9 w-9 rounded-lg grid place-items-center mt-0.5" style={{ background: "var(--overlay)" }}><X size={15} style={{ color: "var(--text-muted)" }} /></button>
              </div>
            ))}
            <button onClick={addV} data-testid="kin-add-venture" className="flex items-center gap-1.5 text-xs gold-text"><Plus size={14} /> Add another</button>
          </div>
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: "var(--text-secondary)" }}>LinkedIn</label>
          <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="linkedin.com/in/…" data-testid="kin-linkedin" className={field} style={fstyle} />
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={onCancel} className="flex-1 rounded-xl py-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }} data-testid="kin-cancel">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="kin-save-profile" className="flex-1 rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-1.5" style={{ background: "var(--accent)", color: "#fff" }}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save profile"}
        </button>
      </div>
    </div>
  );
}

function Feed({ onEditProfile }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [editing, setEditing] = useState(null); // post being edited

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/kinworks/feed"); setPosts(data); }
    catch { setPosts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/kinworks/feed/${p.id}`); setPosts((arr) => arr.filter((x) => x.id !== p.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fmt = (d) => { try { return new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div data-testid="kin-feed">
      <button onClick={onEditProfile} data-testid="kin-edit-profile" className="flex items-center gap-1.5 text-xs mb-3 gold-text"><Pencil size={13} /> Edit my business profile</button>
      {loading ? (
        <div className="grid place-items-center py-10"><Loader2 className="animate-spin gold-text" /></div>
      ) : posts.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: "var(--text-muted)" }}>No posts yet. Tap + to share a business update.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="card-surface rounded-2xl p-3.5" data-testid={`kin-post-${p.id}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="h-7 w-7 rounded-full grid place-items-center text-[11px] font-bold" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
                  {(p.author?.name || "?").charAt(0)}
                </div>
                <span className="text-sm font-semibold flex-1">{p.author?.name}{p.user_id === user?.id ? " (you)" : ""}</span>
                {p.user_id === user?.id && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditing(p); setComposeOpen(true); }} data-testid={`kin-post-edit-${p.id}`} className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: "var(--overlay)" }}><Pencil size={13} style={{ color: "var(--text-secondary)" }} /></button>
                    <button onClick={() => del(p)} data-testid={`kin-post-delete-${p.id}`} className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: "var(--overlay)" }}><Trash2 size={13} style={{ color: "var(--text-secondary)" }} /></button>
                  </div>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{p.content}</p>
              <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>{fmt(p.created_at)}{p.edited_at ? " · edited" : ""}</div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => { setEditing(null); setComposeOpen(true); }} data-testid="kin-compose-btn"
        className="fixed right-6 z-[95] h-14 w-14 rounded-full grid place-items-center shadow-xl"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)", background: "var(--accent)", color: "#fff" }}>
        <Plus size={26} />
      </button>

      {composeOpen && (
        <KinComposeModal post={editing} onClose={() => { setComposeOpen(false); setEditing(null); }}
          onSaved={(saved, isEdit) => {
            setComposeOpen(false); setEditing(null);
            setPosts((arr) => isEdit ? arr.map((x) => x.id === saved.id ? saved : x) : [saved, ...arr]);
          }} />
      )}
    </div>
  );
}

function KinComposeModal({ post, onClose, onSaved }) {
  const [content, setContent] = useState(post?.content || "");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  useAutoGrow(ref, content, 8);

  const submit = async () => {
    if (!content.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = post
        ? await api.put(`/kinworks/feed/${post.id}`, { content: content.trim() })
        : await api.post("/kinworks/feed", { content: content.trim() });
      setDone(true);
      setTimeout(() => onSaved(data, !!post), 650);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[96] flex flex-col" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="kin-compose-modal">
      <div className="mt-auto rounded-t-3xl" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4">
          <span className="font-serif-display text-2xl">{post ? "Edit post" : "New post"}</span>
          <button onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "var(--overlay)" }} data-testid="kin-compose-close"><X size={18} /></button>
        </div>
        <div className="px-5 pb-8">
          {done ? (
            <div className="py-10 text-center" data-testid="kin-compose-done">
              <div className="h-14 w-14 rounded-full grid place-items-center mx-auto mb-3" style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)" }}><Check size={28} className="gold-text" /></div>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Posted!</p>
            </div>
          ) : (
            <>
              <textarea ref={ref} value={content} rows={3} onChange={(e) => setContent(e.target.value)} autoFocus
                placeholder="Share a business update, win, or ask…" data-testid="kin-compose-input"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16, maxHeight: "13rem" }} />
              <button onClick={submit} disabled={saving || !content.trim()} data-testid="kin-compose-submit"
                className="w-full rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 mt-3" style={{ background: "var(--accent)", color: "#fff", opacity: (saving || !content.trim()) ? 0.6 : 1 }}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} {post ? "Save changes" : "Post"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Members({ onEditProfile }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/kinworks/members"); setMembers(data); }
      catch { setMembers([]); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="grid place-items-center py-10"><Loader2 className="animate-spin gold-text" /></div>;
  if (active) return <KinProfileView profile={active} onBack={() => setActive(null)} />;

  return (
    <div data-testid="kin-members">
      <div className="grid grid-cols-3 gap-3">
        {members.map((m) => (
          <button key={m.id} onClick={() => setActive(m)} data-testid={`kin-member-${m.user_id}`} className="flex flex-col items-center text-center">
            {m.author?.photo
              ? <img src={fileUrl(m.author.photo)} alt="" className="h-16 w-16 rounded-full object-cover gold-border" />
              : <div className="h-16 w-16 rounded-full grid place-items-center font-serif-display text-2xl gold-border" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)" }}>{(m.author?.name || "?").charAt(0)}</div>}
            <div className="text-xs font-semibold mt-1.5 leading-tight">{m.author?.name}</div>
            {m.headline && <div className="text-[10px] leading-tight mt-0.5" style={{ color: "var(--text-muted)" }}>{m.headline}</div>}
          </button>
        ))}
      </div>
      {members.length === 0 && <p className="text-sm text-center py-8" style={{ color: "var(--text-muted)" }}>No members yet.</p>}
    </div>
  );
}

function KinProfileView({ profile: m, onBack }) {
  const field = (label, value) => value ? (
    <div className="mb-4" data-testid={`kin-view-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>{value}</div>
    </div>
  ) : null;
  return (
    <div data-testid="kin-profile-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm mb-4" style={{ color: "var(--text-secondary)" }} data-testid="kin-view-back"><ArrowLeft size={16} /> Members</button>
      <div className="flex flex-col items-center text-center mb-5">
        {m.author?.photo
          ? <img src={fileUrl(m.author.photo)} alt="" className="h-24 w-24 rounded-full object-cover gold-border" />
          : <div className="h-24 w-24 rounded-full grid place-items-center font-serif-display text-4xl gold-border" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--accent)" }}>{(m.author?.name || "?").charAt(0)}</div>}
        <div className="font-serif-display text-2xl mt-3">{m.author?.name}</div>
        {m.headline && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{m.headline}</div>}
      </div>
      {field("Skills & experience", m.skills)}
      {field("Career / business aspirations", m.aspirations)}
      {(m.ventures || []).length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>Current ventures / projects</div>
          <div className="space-y-1.5">
            {m.ventures.map((v, i) => (
              <a key={i} href={v.link || "#"} target="_blank" rel="noreferrer" onClick={(e) => !v.link && e.preventDefault()}
                className="flex items-center gap-1.5 text-sm gold-text" data-testid={`kin-view-venture-${i}`}>
                <ExternalLink size={13} /> {v.name || v.link}
              </a>
            ))}
          </div>
        </div>
      )}
      {m.linkedin && (
        <a href={m.linkedin.startsWith("http") ? m.linkedin : `https://${m.linkedin}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm rounded-xl px-4 py-2.5" style={{ background: "var(--overlay)", color: "var(--accent)" }} data-testid="kin-view-linkedin">
          <Linkedin size={15} /> View LinkedIn
        </a>
      )}
    </div>
  );
}

